import { selectCleanedImageData } from '~/features/background/useCleanedImageData';
import { useStore } from '~/store';
import type { RootState } from '~/store/slices';

/** True when `enterSprite()` would succeed — mirrors its precondition. */
export const selectCanCommit = (s: RootState): boolean => s.source.imageData !== null;

/**
 * Stable hash of every input the matte pipeline depends on. Diverges from the
 * `committedKey` whenever the user changes a matte control after committing —
 * the sprite step uses the divergence to flag stale commits (P5 polish).
 */
export function computeMatteKey(s: RootState): string {
  const b = s.background;
  const wm = s.watermark;
  return JSON.stringify({
    src: s.source.hash,
    rev: s.source.revision,
    bg: {
      enabled: b.enabled,
      tolerance: b.tolerance,
      hardEdge: b.hardEdge,
      erode: b.erode,
      feather: b.feather,
      despillStrength: b.despillStrength,
      despillRadius: b.despillRadius,
      floodFill: b.floodFill,
      maxEnclosedIsland: b.maxEnclosedIsland,
      bgColors: b.bgColors,
    },
    wm: { enabled: wm.enabled, region: wm.region },
  });
}

/** "Back to matte" — return without re-running the pipeline. */
export function enterMatte(): void {
  const s = useStore.getState();
  s.setDrawMode('none');
  s.setStep('matte');
}

/**
 * "Continue" — snapshot the live matte output into `committedCleaned` and
 * advance to the sprite step. No-op (already there) if step is already sprite.
 * Returns false when there is nothing to commit (no source / pipeline output).
 */
export function enterSprite(): boolean {
  const s = useStore.getState();
  if (s.workflow.step === 'sprite') return true;
  const cleaned = s.background.cleaned ?? selectCleanedImageData(s);
  if (!cleaned) return false;
  s.setCommitted({ cleaned, key: computeMatteKey(s) });
  s.setDrawMode('none');
  s.setStep('sprite');
  return true;
}

/** Recompute the committed snapshot in place (no step change). */
export function commitMatte(): boolean {
  const s = useStore.getState();
  if (!s.background.cleaned) return false;
  s.setCommitted({ cleaned: s.background.cleaned, key: computeMatteKey(s) });
  return true;
}
